/**
 * Unified Service Types and Interfaces
 * Provides type safety and consistency across all services
 */

// Core HTTP types
export interface RequestOptions {
  headers?: Record<string, string>;
  timeout?: number;
  cache?: RequestCache;
  credentials?: RequestCredentials;
  retries?: number;
  retryDelay?: number;
  signal?: AbortSignal;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
  offset?: number;
}

export interface SortParams {
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export interface FilterParams {
  [key: string]: any;
}

export interface QueryParams
  extends PaginationParams,
    SortParams,
    FilterParams {}

// Service Response Types
export interface ServiceResponse<T = any> {
  data: T;
  meta?: {
    pagination?: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
    timing?: {
      requestId: string;
      duration: number;
      timestamp: string;
    };
  };
  status: number;
  statusText: string;
}

// Error Types
export enum ServiceErrorCode {
  NETWORK_ERROR = "NETWORK_ERROR",
  TIMEOUT_ERROR = "TIMEOUT_ERROR",
  VALIDATION_ERROR = "VALIDATION_ERROR",
  AUTHENTICATION_ERROR = "AUTHENTICATION_ERROR",
  AUTHORIZATION_ERROR = "AUTHORIZATION_ERROR",
  NOT_FOUND_ERROR = "NOT_FOUND_ERROR",
  RATE_LIMIT_ERROR = "RATE_LIMIT_ERROR",
  SERVER_ERROR = "SERVER_ERROR",
  UNKNOWN_ERROR = "UNKNOWN_ERROR",
}

export interface ServiceError extends Error {
  code: ServiceErrorCode;
  status?: number;
  details?: any;
  service?: string;
  context?: string;
  timestamp: string;
  requestId?: string;
  retry?: boolean;
}

// Cache Types
export interface CacheOptions {
  key: string;
  ttl?: number;
  tags?: string[];
  revalidate?: boolean;
}

export interface CacheStrategy {
  enabled: boolean;
  defaultTtl: number;
  maxAge?: number;
  staleWhileRevalidate?: number;
  strategy: "cache-first" | "network-first" | "cache-only" | "network-only";
}

// Service Configuration
export interface ServiceConfig {
  name: string;
  baseURL?: string;
  timeout?: number;
  retries?: number;
  retryDelay?: number;
  cache?: CacheStrategy;
  enableLogging?: boolean;
  enableMetrics?: boolean;
  headers?: Record<string, string>;
  credentials?: RequestCredentials;
}

// Interceptor Types
export interface RequestInterceptor {
  name: string;
  priority: number;
  onRequest: (
    config: RequestOptions & { url: string },
  ) => Promise<RequestOptions & { url: string }>;
  onError?: (error: ServiceError) => Promise<ServiceError>;
}

export interface ResponseInterceptor {
  name: string;
  priority: number;
  onResponse: <T>(response: ServiceResponse<T>) => Promise<ServiceResponse<T>>;
  onError?: (error: ServiceError) => Promise<ServiceError>;
}

// Service Interface
export interface IService {
  readonly name: string;
  readonly config: ServiceConfig;

  healthCheck(): Promise<{ status: "healthy" | "unhealthy"; details?: any }>;

  getMetrics(): Promise<ServiceMetrics>;

  clearCache(pattern?: string): Promise<void>;
}

// Metrics Types
export interface ServiceMetrics {
  requests: {
    total: number;
    successful: number;
    failed: number;
    averageResponseTime: number;
  };
  cache: {
    hits: number;
    misses: number;
    hitRate: number;
  };
  errors: {
    byCode: Record<ServiceErrorCode, number>;
    total: number;
  };
  uptime: number;
  lastHealthCheck: string;
}

// Validation Types
export interface ValidationRule {
  field: string;
  required?: boolean;
  type?: "string" | "number" | "boolean" | "array" | "object";
  min?: number;
  max?: number;
  pattern?: RegExp;
  custom?: (value: any) => boolean | string;
}

export interface ValidationSchema {
  rules: ValidationRule[];
  allowUnknown?: boolean;
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
}

export interface ValidationError {
  field: string;
  message: string;
  value?: any;
}

// Event Types for Service Lifecycle
export interface ServiceEvent {
  type: "request" | "response" | "error" | "cache-hit" | "cache-miss";
  service: string;
  timestamp: string;
  data?: any;
}

export type ServiceEventHandler = (event: ServiceEvent) => void;

// Factory Types
export interface ServiceFactory {
  create<T extends IService>(config: ServiceConfig): T;

  register<T extends IService>(service: T): void;

  get<T extends IService>(name: string): T | undefined;

  list(): string[];

  healthCheckAll(): Promise<Record<string, any>>;
}

// Context Types for Request Tracking
export interface RequestContext {
  requestId: string;
  service: string;
  operation: string;
  startTime: number;
  user?: {
    id: string;
    email?: string;
  };
  metadata?: Record<string, any>;
}

// Circuit Breaker Types
export interface CircuitBreakerConfig {
  failureThreshold: number;
  recoveryTimeout: number;
  monitorTimeout: number;
}

export interface CircuitBreakerState {
  state: "closed" | "open" | "half-open";
  failureCount: number;
  lastFailureTime?: number;
  nextAttemptTime?: number;
}

// Rate Limiting Types
export interface RateLimitConfig {
  requests: number;
  window: number; // in milliseconds
  burst?: number;
}

export interface RateLimitState {
  requests: number;
  windowStart: number;
  blocked: boolean;
}
